import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAskAiMutation } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { addAiMessage, setAiBusy, takeAiAsk, useAiConversation } from '../aiConversation';
import type { IAiPageContext } from './IAiPageContext';

/**
 * Надсилання питання ШІ-асистенту (прототип v4 ask): порожнє питання чи питання, поки асистент «думає», ігнорується; повідомлення
 * користувача → «Думаю…» → відповідь сервера POST /ai/ask (Markdown). Сервер без цього API — чесна відповідь, що ШІ-сервер не під'єднано,
 * з контекстом; збій — «На жаль, асистент зараз недоступний.». Питання з черги (подія po:ai-ask) надсилаються одразу, щойно панель готова.
 */
export function useAiAsk(ws: string, context: IAiPageContext): (text: string) => void {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const conversation = useAiConversation();
  const [askServer] = useAskAiMutation();
  const ask = useCallback((text: string) => {
    const q = text.trim();
    if (!q || conversation.busy) return;
    const messages = [...conversation.messages, { role: 'user' as const, text: q }];
    addAiMessage({ role: 'user', text: q });
    setAiBusy(true);
    askServer({ workspaceId: ws, context: context.ask, messages, lang }).unwrap()
      .then((res) => addAiMessage({ role: 'assistant', text: res.available && res.reply ? res.reply : t('shell.aiOffline', { context: context.path }) }))
      .catch(() => addAiMessage({ role: 'assistant', text: t('aiPanel.failed') }))
      .finally(() => setAiBusy(false));
  }, [conversation.busy, conversation.messages, askServer, ws, context.ask, context.path, lang, t]);
  useEffect(() => {
    if (conversation.busy || !conversation.pending.length) return;
    const next = takeAiAsk();
    if (next) ask(next);
  }, [conversation.busy, conversation.pending, ask]);
  return ask;
}
